import React from 'react';
import {
    X, CheckCircle, AlertCircle, Loader2, ToggleLeft, ToggleRight,
    Eye, EyeOff, KeyRound, ShieldCheck, Server, Activity, Database
} from 'lucide-react';

// ─── Modal Wrapper ────────────────────────────────────────────────────────────

export const Modal = ({ isOpen, onClose, title, children }) => {
    if (!isOpen) return null;
    return (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50">
            <div className="bg-slate-900/95 border border-emerald-500/40 rounded-2xl shadow-2xl p-8 max-w-lg w-full relative">
                <button onClick={onClose} className="absolute top-4 right-4 text-white/60 hover:text-white transition-colors">
                    <X className="w-6 h-6" />
                </button>
                {title && <h3 className="text-2xl font-bold mb-6 text-center text-white">{title}</h3>}
                {children}
            </div>
        </div>
    );
};

// ─── Alert Banner ─────────────────────────────────────────────────────────────

export const AlertBanner = ({ success, error }) => {
    if (!success && !error) return null;
    const isSuccess = !!success;
    return (
        <div className={`p-4 rounded-xl flex items-center space-x-3 border backdrop-blur-sm ${isSuccess ? 'bg-green-500/20 border-green-500/40 text-green-200' : 'bg-red-500/20 border-red-500/40 text-red-200'}`}>
            {isSuccess ? <CheckCircle className="w-5 h-5 flex-shrink-0" /> : <AlertCircle className="w-5 h-5 flex-shrink-0" />}
            <span className="font-medium">{success || error}</span>
        </div>
    );
};

// ─── Form Field ───────────────────────────────────────────────────────────────

export const FormField = ({ label, icon: Icon, children }) => (
    <div>
        <label className="block text-white/90 text-sm font-medium mb-2">
            {Icon && <Icon className="w-4 h-4 inline mr-2" />}
            {label}
        </label>
        {children}
    </div>
);

export const TextInput = ({ value, onChange, placeholder, disabled, type = 'text', className = '' }) => (
    <input
        type={type}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        className={`w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500/50 transition-all duration-300 ${className}`}
    />
);

export const PasswordInput = ({ value, onChange, disabled, show, onToggle }) => (
    <div className="relative">
        <TextInput value={value} onChange={onChange} placeholder="Enter password..." disabled={disabled} type={show ? 'text' : 'password'} />
        <button type="button" onClick={onToggle} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/60 hover:text-white transition-colors">
            {show ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
        </button>
    </div>
);

// ─── App Status Toggle ────────────────────────────────────────────────────────

export const AppStatusToggle = ({ appName, isActive, isLoading, isUpdating, onToggle }) => {
    const unknown = isActive === undefined && !isLoading;
    return (
        <div className="flex items-center justify-between bg-white/5 rounded-lg p-2">
            <div className="flex items-center space-x-2">
                <span className="text-xs text-white/60">App Status:</span>
                <span className={`flex items-center space-x-1 px-2 py-1 rounded-full text-xs ${isActive ? 'bg-green-500/20 text-green-300' : unknown ? 'bg-gray-500/20 text-gray-300' : 'bg-red-500/20 text-red-300'}`}>
                    <span className={`w-2 h-2 rounded-full ${isActive ? 'bg-green-400' : unknown ? 'bg-gray-400' : 'bg-red-400'}`} />
                    {isLoading ? 'Loading...' : isActive ? 'Active' : unknown ? 'Unknown' : 'Inactive'}
                </span>
            </div>
            <button
                onClick={e => { e.stopPropagation(); onToggle(); }}
                disabled={isUpdating || isLoading || unknown}
                className={`flex items-center space-x-1 px-2 py-1 rounded-lg text-xs transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed ${isActive ? 'bg-red-500/20 border border-red-500/40 text-red-200 hover:bg-red-500/30' : 'bg-green-500/20 border border-green-500/40 text-green-200 hover:bg-green-500/30'}`}
            >
                {isUpdating ? <Loader2 className="w-3 h-3 animate-spin" /> : isActive ? <ToggleRight className="w-4 h-4" /> : <ToggleLeft className="w-4 h-4" />}
                <span>{isUpdating ? 'Updating...' : isActive ? 'Deactivate' : 'Activate'}</span>
            </button>
        </div>
    );
};

// ─── Queue Icons ──────────────────────────────────────────────────────────────

export const QUEUE_ICONS = {
    '0': <Server className="w-4 h-4" />,
    '1': <Activity className="w-4 h-4" />,
    '2': <Database className="w-4 h-4" />,
};

// ─── License Modal ────────────────────────────────────────────────────────────

export const LicenseModal = ({ isOpen, onClose, licenseNo, setLicenseNo, licenseError, onSubmit }) => (
    <Modal isOpen={isOpen} onClose={onClose} title="License Key Required">
        <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-emerald-600/20 border border-emerald-600/40 mb-6">
                <KeyRound className="h-8 w-8 text-emerald-300" />
            </div>
            <p className="text-white/70 mb-6">Enter your license number to proceed.</p>
            <input
                type="text"
                value={licenseNo}
                onChange={e => { setLicenseNo(e.target.value); }}
                placeholder="Enter license number..."
                className="w-full px-4 py-4 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 transition-all"
            />
            {licenseError && <p className="text-red-400 text-sm mt-2">{licenseError}</p>}
            <div className="mt-8 flex justify-end space-x-4">
                <button onClick={onClose} className="px-6 py-3 rounded-xl bg-white/10 hover:bg-white/20 border border-white/10 transition-colors">Cancel</button>
                <button onClick={onSubmit} className="px-6 py-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 font-semibold transition-colors">Submit</button>
            </div>
        </div>
    </Modal>
);

// ─── Confirm Modal ────────────────────────────────────────────────────────────

export const ConfirmModal = ({ isOpen, onClose, config, onConfirm, isUpdating }) => (
    <Modal isOpen={isOpen} onClose={onClose} title="Confirm Action">
        {config && (
            <div className="text-center">
                <div className={`mx-auto flex items-center justify-center h-16 w-16 rounded-full border mb-6 ${config.STATUS === 'ACTIVE' ? 'bg-red-600/20 border-red-600/40' : 'bg-green-600/20 border-green-600/40'}`}>
                    <ShieldCheck className={`h-8 w-8 ${config.STATUS === 'ACTIVE' ? 'text-red-300' : 'text-green-300'}`} />
                </div>
                <p className="text-white/70 mb-6">
                    Are you sure you want to <strong>{config.STATUS === 'ACTIVE' ? 'deactivate' : 'activate'}</strong> the WhatsApp configuration for <strong className="text-emerald-300">{config.appName}</strong>?
                </p>
                <div className="mt-8 flex justify-end space-x-4">
                    <button onClick={onClose} className="px-6 py-3 rounded-xl bg-white/10 hover:bg-white/20 border border-white/10 transition-colors">Cancel</button>
                    <button
                        onClick={onConfirm}
                        className={`px-6 py-3 rounded-xl font-semibold transition-colors ${config.STATUS === 'ACTIVE' ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'}`}
                    >
                        {isUpdating ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : `Yes, ${config.STATUS === 'ACTIVE' ? 'Deactivate' : 'Activate'}`}
                    </button>
                </div>
            </div>
        )}
    </Modal>
);